// src/screens/GestionFacturasScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, addDoc, Timestamp, doc, deleteDoc, updateDoc, getDocs, limit, arrayUnion, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { openAuthenticatedInvoice } from '../utils/invoiceDownload';
import { useAuth } from '../context/AuthContext';
// eslint-disable-next-line no-unused-vars
import { IoArrowBack, IoTrash, IoAdd, IoDocumentTextOutline, IoPersonAddOutline, IoBusinessOutline, IoWarningOutline, IoCreateOutline, IoCloudUploadOutline, IoCheckmarkCircleOutline, IoTrashOutline, IoSparklesOutline, IoCloseCircleOutline, IoFlashOutline, IoCloudDownloadOutline, IoReceiptOutline } from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import RegistrarGastoModal from '../components/RegistrarGastoModal';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './GestionFacturasScreen.css';
import { scanInvoice } from '../utils/ocrService';
import { DEFAULT_CHART_OF_ACCOUNTS, ACCOUNT_TYPES } from '../utils/accounting/constants';
// eslint-disable-next-line no-unused-vars
import { postLedgerEntry, ACCOUNTS, getAccountMap } from '../utils/accountingUtils';
import { getApprovalRules, validateInvoiceRules } from '../utils/approvalService';

moment.locale('es');

function GestionFacturasScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();

    // Check context from navigation state ('gastos', 'nominas', 'aprobaciones')
    const viewContext = location.state?.viewContext || 'gastos';

    // Tabs: 'buzon' (Invoices) | 'proveedores' (Internal Directory) | 'saldos' (Balance Report)
    const [activeTab, setActiveTab] = useState(viewContext === 'nominas' ? 'nominas' : 'buzon');

    // Data States
    const [facturas, setFacturas] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [recordatoriosNomina, setRecordatoriosNomina] = useState([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [showAccountSuggestions, setShowAccountSuggestions] = useState(false);
    const [accountSearchTerm, setAccountSearchTerm] = useState('');
    const [localFileCache, setLocalFileCache] = useState({}); // Cache for recently uploaded files (OCR bypass)
    const [loading, setLoading] = useState(true);

    // Filtering
    const [filterStatus, setFilterStatus] = useState('todos'); // todos, pendientes, pagados

    // Modal States
    const [showRegistrarGasto, setShowRegistrarGasto] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [showNewProvider, setShowNewProvider] = useState(false);
    const [successNotification, setSuccessNotification] = useState(null);
    const [errorNotification, setErrorNotification] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [showScanConfirm, setShowScanConfirm] = useState(false);
    const [deleteProviderConfirm, setDeleteProviderConfirm] = useState(null);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [invoiceToApprove, setInvoiceToApprove] = useState(null);

    // New Provider Form State
    const [newProviderName, setNewProviderName] = useState('');
    const [newProviderRfc, setNewProviderRfc] = useState('');
    const [newProviderCategory, setNewProviderCategory] = useState('');

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Inbox / Processing States
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'processing'
    const [processingInvoice, setProcessingInvoice] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [showProviderSuggestions, setShowProviderSuggestions] = useState(false);

    // --- File Handlers ---
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleBulkUpload(e.dataTransfer.files);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files.length > 0) {
            handleBulkUpload(e.target.files);
        }
    };

    const handleBulkUpload = async (files) => {
        setLoading(true);
        let uploadedCount = 0;

        try {
            const uploadPromises = Array.from(files).map(async (file) => {
                const storageRef = ref(storage, `facturas/${userData.condominioId}/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                const fileUrl = await getDownloadURL(storageRef);

                const docRef = await addDoc(collection(db, "gastos"), {
                    condominioId: userData.condominioId,
                    fecha: Timestamp.now(),
                    proveedor: "", // Empty for draft
                    descripcion: `Documento cargado: ${file.name}`,
                    monto: 0,
                    estatus: 'borrador', // Draft status
                    comprobanteUrl: fileUrl,
                    tipo: 'egreso',
                    registradoPor: userData.uid,
                    fechaRegistro: Timestamp.now()
                });

                // Cache file for OCR bypass
                setLocalFileCache(prev => ({ ...prev, [docRef.id]: file }));

                uploadedCount++;
            });

            await Promise.all(uploadPromises);
            setSuccessNotification(`${uploadedCount} documento(s) subido(s) a la bandeja "Por Procesar".`);
        } catch (error) {
            console.error(error);
            setErrorNotification("Error al subir documentos: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper to open a draft for processing
    const openDraft = (invoice) => {
        // Find Account Name for Search Term
        const acc = expenseAccounts.find(a => a.codigo === invoice.cuentaCodigo);
        setAccountSearchTerm(acc ? `${acc.codigo} - ${acc.nombre}` : '');

        const mappedDistribucion = invoice.distribucion || [
            { 
                cuentaCodigo: invoice.cuentaCodigo || '', 
                subtotal: invoice.subtotal || invoice.monto || '',
                searchTerm: acc ? `${acc.codigo} - ${acc.nombre}` : (invoice.cuentaCodigo || '')
            }
        ];

        setProcessingInvoice({
            id: invoice.id,
            file: localFileCache[invoice.id] || null, // Prefer cached file object
            previewUrl: invoice.comprobanteUrl,
            params: {
                fecha: invoice.fecha ? moment(invoice.fecha.toDate()).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                monto: invoice.monto || '',
                subtotal: invoice.subtotal || invoice.monto || '',
                iva: invoice.iva || '0',
                proveedor: invoice.proveedor || '',
                descripcion: invoice.descripcion || '',
                cuentaCodigo: invoice.cuentaCodigo || '',
                distribucion: mappedDistribucion
            }
        });
        setViewMode('processing');
    };

    useEffect(() => {
        if (!userData?.condominioId) return;

        // 1. Listen to 'gastos' (Facturas)
        // Linking query to existing structure but focusing on Supplier Invoices
        // We filter by 'tipo' == 'egreso' (expenses)
        const qGastos = query(
            collection(db, "gastos"),
            where("condominioId", "==", userData.condominioId),
            orderBy("fecha", "desc"),
            limit(150)
        );

        const unsubGastos = onSnapshot(qGastos, (snapshot) => {
            const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setFacturas(list);
        });

        // 2. Listen to 'condominio_proveedores' (Internal Directory)
        const qProv = query(
            collection(db, "condominio_proveedores"),
            where("condominioId", "==", userData.condominioId),
            orderBy("nombre", "asc"),
            limit(500)
        );

        const unsubProv = onSnapshot(qProv, (snapshot) => {
            const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setProveedores(list);
            setLoading(false);
        });

        // 2.5 Listen to 'empleados' for Payroll Reminders
        const qEmpleados = query(
            collection(db, "empleados"),
            where("condominioId", "==", userData.condominioId),
            where("estatus", "==", "Activo"),
            limit(500)
        );

        const unsubEmpleados = onSnapshot(qEmpleados, (snapshot) => {
            const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setEmpleados(list);
        });

        // 3. Fetch Expense Accounts (Chart of Accounts)
        // Try to fetch custom accounts first, if empty, use default
        const qCuentas = query(
            collection(db, "cuentas_contables"),
            where("condominioId", "==", userData.condominioId)
        );

        getDocs(qCuentas).then((snap) => {
            let dbAccounts = [];
            if (!snap.empty) {
                const rawDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                // Filter DB accounts for valid codes (Allow 5xxx and 6xxx, plus explicit types)
                dbAccounts = rawDocs.filter(a => {
                    const code = a.codigo ? a.codigo.toString() : '';
                    return a.codigo && (
                        a.tipo === 'GASTO' ||
                        a.tipo === ACCOUNT_TYPES.GASTO ||
                        a.tipo === 'COSTO' ||
                        a.tipo === 'EGRESO' ||
                        code.startsWith('5') || // Costos
                        code.startsWith('6')    // Gastos
                    );
                });
            }

            // Get Defaults
            const defaultAccounts = DEFAULT_CHART_OF_ACCOUNTS.filter(a => a.tipo === ACCOUNT_TYPES.GASTO && a.nivel > 2);

            // MERGE Logic: prefer DB, fill gaps with Defaults
            const accountMap = new Map();

            // Add Defaults first
            defaultAccounts.forEach(acc => accountMap.set(acc.codigo.toString(), { ...acc, source: 'default' }));

            // Overwrite/Add DB accounts
            dbAccounts.forEach(acc => accountMap.set(acc.codigo.toString(), { ...acc, source: 'db' }));

            // Convert back to array and sort
            const mergedAccounts = Array.from(accountMap.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));

            setExpenseAccounts(mergedAccounts);
        });

        return () => {
            unsubGastos();
            unsubProv();
            unsubEmpleados();
        };

    }, [userData]);

    // Generador de Recordatorios de Nómina basado en Empleados y periodicidad
    useEffect(() => {
        if (!empleados || empleados.length === 0) {
            setRecordatoriosNomina([]);
            return;
        }

        const recordatorios = [];
        const hoy = moment();
        const diasDelMes = hoy.daysInMonth();
        const diaActual = hoy.date();

        empleados.forEach(emp => {
            let requiereRecordatorio = false;
            let periodoDesc = "";

            if (emp.periodicidadPago === 'Semanal') {
                // Simplificación: Suponiendo pago los viernes (ej. día 5)
                if (hoy.day() === 4 || hoy.day() === 5) { // Jueves o Viernes
                    requiereRecordatorio = true;
                    periodoDesc = "Nómina Semanal";
                }
            } else if (emp.periodicidadPago === 'Catorcenal') {
                // Cada 14 días. Para no complicarlo, asomamos cada 13/14 o 27/28
                if (diaActual >= 13 && diaActual <= 15) { requiereRecordatorio = true; periodoDesc = "Nómina Catorcenal (Q1)"; }
                if (diaActual >= 27 && diaActual <= 29) { requiereRecordatorio = true; periodoDesc = "Nómina Catorcenal (Q2)"; }
            } else {
                // Quincenal / Mensual por defecto (Evaluar los días 14,15 y fin de mes)
                if (diaActual >= 13 && diaActual <= 15) {
                    requiereRecordatorio = true;
                    periodoDesc = "1ra Quincena";
                }
                if (diaActual >= diasDelMes - 2 && diaActual <= diasDelMes) {
                    requiereRecordatorio = true;
                    periodoDesc = "2da Quincena";
                }
            }

            if (requiereRecordatorio) {
                // Check si ya se guardó una nómina reciente para este empleado
                const nominaGuardada = facturas.find(f =>
                    f.proveedorId === 'NOMINA_INTERNA' &&
                    f.proveedorNombre?.includes(emp.nombre) &&
                    moment(f.fecha?.toDate()).isSame(hoy, 'month') && // Mismo mes
                    (f.descripcion?.includes(periodoDesc) || (diaActual <= 15 ? moment(f.fecha?.toDate()).date() <= 15 : moment(f.fecha?.toDate()).date() > 15)) // Misma quincena aprox
                );

                if (!nominaGuardada) {
                    recordatorios.push({
                        id: `recordatorio_${emp.id}`,
                        isRecordatorio: true,
                        empleado: emp,
                        proveedorId: 'NOMINA_INTERNA',
                        proveedor: `Nómina: ${emp.nombre} ${emp.apellidos}`,
                        descripcion: `🔔 Recordatorio: Correr ${periodoDesc}`,
                        estatus: 'borrador', // Se visualiza en Por Procesar
                        monto: 0,
                        fecha: Timestamp.now()
                    });
                }
            }
        });

        setRecordatoriosNomina(recordatorios);
    }, [empleados, facturas]);

    // --- Actions ---

    const handleSaveInvoice = async () => {
        if (!processingInvoice || !processingInvoice.params.proveedor || !processingInvoice.params.monto) {
            alert("Por favor completa el proveedor y el monto.");
            return;
        }

        // Validate Account Selection
        if (!processingInvoice.params.cuentaCodigo) {
            alert("Por favor selecciona una Cuenta Contable (Categoría).");
            return;
        }

        setLoading(true);
        try {
            let fileUrl = processingInvoice.previewUrl || null;
            if (processingInvoice.file) {
                const storageRef = ref(storage, `facturas/${userData.condominioId}/${Date.now()}_${processingInvoice.file.name}`);
                await uploadBytes(storageRef, processingInvoice.file);
                fileUrl = await getDownloadURL(storageRef);
            }

            // --- HELPER: Ensure Account Exists in DB ---
            const ensureAccountInDB = async (codeOrAccount) => {
                let acc = null;
                if (typeof codeOrAccount === 'string') {
                    // Find by code in defaults
                    acc = DEFAULT_CHART_OF_ACCOUNTS.find(a => a.codigo === codeOrAccount);
                } else {
                    acc = codeOrAccount;
                }

                if (!acc) return null;

                // Check if exists in DB (Query is safest)
                const q = query(
                    collection(db, "cuentas_contables"),
                    where("condominioId", "==", userData.condominioId),
                    where("codigo", "==", acc.codigo)
                );
                const snap = await getDocs(q);

                if (!snap.empty) {
                    return snap.docs[0].id;
                } else {
                    // Create it
                    const newRef = await addDoc(collection(db, "cuentas_contables"), {
                        condominioId: userData.condominioId,
                        codigo: acc.codigo,
                        nombre: acc.nombre,
                        tipo: acc.tipo,
                        nivel: acc.nivel,
                        padre: acc.padre || null,
                        numero: acc.codigo,
                        descripcion: acc.nombre,
                        fechaCreacion: Timestamp.now()
                    });
                    return newRef.id;
                }
            };

            // 1. Resolve Expense Account (Materialize if needed)
            // Robust lookup: Handle case where 'cuentaCodigo' might be Name (if value was missing) or Code
            const searchVal = processingInvoice.params.cuentaCodigo ? processingInvoice.params.cuentaCodigo.toString() : '';

            let selectedExpenseAcc =
                expenseAccounts.find(a => (a.codigo && a.codigo.toString() === searchVal)) ||
                DEFAULT_CHART_OF_ACCOUNTS.find(a => (a.codigo && a.codigo.toString() === searchVal));

            // Fallback: Try match by Name 
            if (!selectedExpenseAcc) {
                selectedExpenseAcc = expenseAccounts.find(a => a.nombre === searchVal) ||
                    DEFAULT_CHART_OF_ACCOUNTS.find(a => a.nombre === searchVal);
            }

            const finalAccountId = await ensureAccountInDB(selectedExpenseAcc);
            const finalAccountName = selectedExpenseAcc?.nombre || 'Gasto General';

            // 2. Resolve Provider Account (Materialize if needed)
            const providerAccountId = await ensureAccountInDB(ACCOUNTS.PROVEEDORES);

            if (!finalAccountId || !providerAccountId) {
                console.warn("Accounting Warning: Could not resolve IDs", { finalAccountId, providerAccountId });
                alert("Error crítico: No se pudieron asignar las cuentas contables. Intenta seleccionar otra categoría.");
                setLoading(false);
                return;
            }

            // 2.5 Validation (Approval Rules)
            const approvalRules = await getApprovalRules(userData.condominioId);
            const validation = validateInvoiceRules(parseFloat(processingInvoice.params.monto), approvalRules);

            // 3. Create or Update Invoice Document
            const invoiceData = {
                condominioId: userData.condominioId,
                fecha: Timestamp.fromDate(new Date(processingInvoice.params.fecha)),
                proveedor: processingInvoice.params.proveedor,
                descripcion: processingInvoice.params.descripcion || "Gasto sin descripción",
                monto: parseFloat(processingInvoice.params.monto),
                
                // Status Logic: 'por_aprobar' vs 'pendiente' (Ready for Payment)
                estatus: validation.nextStatus,
                
                // OCR Split distribution & taxes
                subtotal: parseFloat(processingInvoice.params.subtotal || processingInvoice.params.monto),
                iva: parseFloat(processingInvoice.params.iva || 0),
                distribucion: processingInvoice.params.distribucion || [],

                // Approval Metadata
                requiresApproval: validation.requiresApproval,
                approvalLevel: validation.approvalLevel,
                approvers: validation.approvers,
                approvalHistory: [], // Log of actions

                comprobanteUrl: fileUrl,
                tipo: 'egreso',

                // Accounting Links
                cuentaId: finalAccountId,
                cuentaContableId: finalAccountId,
                cuentaCodigo: selectedExpenseAcc?.codigo || '', // Ensure we save the resolved code
                cuentaNombre: finalAccountName,
                categoria: finalAccountName,

                registradoPor: userData.uid
            };

            let invoiceRef;
            if (processingInvoice.id && processingInvoice.id !== 'new') {
                // Update existing draft
                const ref = doc(db, "gastos", processingInvoice.id);
                await updateDoc(ref, invoiceData);
                invoiceRef = ref;
            } else {
                // Create new
                invoiceData.fechaRegistro = Timestamp.now();
                invoiceRef = await addDoc(collection(db, "gastos"), invoiceData);
            }

            // 4. Post Ledger Entry (ONLY IF APPROVED)
            if (!validation.requiresApproval) {
                try {
                    await postLedgerEntry(userData.condominioId, {
                        fecha: Timestamp.fromDate(new Date(processingInvoice.params.fecha)),
                        concepto: `Provisión Factura: ${processingInvoice.params.proveedor} - ${processingInvoice.params.descripcion}`,
                        referenciaId: invoiceRef.id,
                        tipoReferencia: 'GASTO',
                        detalles: [
                            { cuentaId: finalAccountId, debe: parseFloat(processingInvoice.params.monto), haber: 0 },
                            { cuentaId: providerAccountId, debe: 0, haber: parseFloat(processingInvoice.params.monto) }
                        ]
                    });
                } catch (e) {
                    console.error("Ledger Error:", e);
                    setErrorNotification("Factura guardada, pero hubo un detalle con la contabilidad: " + e.message);
                }
                setSuccessNotification({
                    title: 'Gasto Registrado',
                    message: 'Factura registrada y contabilizada exitosamente.'
                });
            } else {
                setSuccessNotification({
                    title: 'Aprobación Requerida',
                    message: `Factura guardada. Requiere aprobación (Nivel ${validation.approvalLevel}). Notificando a los miembros del comité por correo.`
                });
            }

            setProcessingInvoice(null);
            setViewMode('list');
        } catch (error) {
            console.error("Error saving invoice:", error);
            setErrorNotification("Error al guardar la factura: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const [editingProvider, setEditingProvider] = useState(null);

    const handleCreateProvider = async (e) => {
        e.preventDefault();
        if (!newProviderName) return;

        try {
            const data = {
                condominioId: userData.condominioId,
                nombre: newProviderName,
                rfc: newProviderRfc || '',
                categoria: newProviderCategory || 'General',
                fechaRegistro: Timestamp.now()
            };

            if (editingProvider) {
                // Update
                const ref = doc(db, "condominio_proveedores", editingProvider.id);
                await updateDoc(ref, {
                    nombre: newProviderName,
                    rfc: newProviderRfc || '',
                    categoria: newProviderCategory || 'General'
                });
                setSuccessNotification("Proveedor actualizado.");
            } else {
                // Create
                await addDoc(collection(db, "condominio_proveedores"), data);
                setSuccessNotification("Proveedor agregado al directorio interno.");
            }

            setNewProviderName('');
            setNewProviderRfc('');
            setNewProviderCategory('');
            setShowNewProvider(false);
            setEditingProvider(null);
        } catch (error) {
            console.error(error);
            setErrorNotification("Error al guardar proveedor: " + error.message);
        }
    };

    const handleDeleteProvider = (id, nombre) => {
        setDeleteProviderConfirm({ id, nombre });
    };

    // --- Render Helpers ---

    // --- Approval Actions ---
    const handleApprove = async (invoice) => {
        // 1. Permission Check
        const canApprove = userData.rol === 'superuser' || userData.rol === 'masteruser' || (invoice.approvers && invoice.approvers.includes(userData.uid)) || (invoice.proveedorId === 'NOMINA_INTERNA' && (userData.rol === 'superuser' || userData.rol === 'masteruser' || userData.rol === 'comite'));

        if (!canApprove) {
            setErrorNotification("No tienes permiso para aprobar esta factura (Nivel asignado a otros usuarios).");
            return;
        }

        setInvoiceToApprove(invoice);
        setShowApproveConfirm(true);
    };

    const executeApproval = async () => {
        if (!invoiceToApprove) return;
        const invoice = invoiceToApprove;
        setShowApproveConfirm(false);
        setInvoiceToApprove(null);

        setLoading(true);
        try {
            // 2. Determine Next Step (Sequential Logic)
            const approvalRules = await getApprovalRules(userData.condominioId);
            // Verify what level we are approving.
            const currentReqLevel = invoice.approvalLevel || 0;
            const isPayroll = invoice.proveedorId === 'NOMINA_INTERNA';

            // Validation returns the NEXT step *after* this level.
            const nextValidation = validateInvoiceRules(invoice.monto || invoice.total, approvalRules, currentReqLevel, isPayroll);

            const invoiceRef = doc(db, "gastos", invoice.id);
            const updates = {
                approvalHistory: arrayUnion({
                    action: isPayroll ? 'approved_payroll' : 'approved',
                    by: userData.uid,
                    userName: userData.nombre,
                    date: Timestamp.now(),
                    level: currentReqLevel
                })
            };

            if (nextValidation.requiresApproval) {
                // Advance to next level
                updates.estatus = 'por_aprobar'; // Stays same
                updates.approvalLevel = nextValidation.approvalLevel;
                updates.approvers = nextValidation.approvers;
                setSuccessNotification({
                    title: 'Aprobación Registrada',
                    message: `Aprobado (Nivel ${currentReqLevel}). Siguiente paso: Aprobación Nivel ${nextValidation.approvalLevel}.`
                });
            } else {
                // Final Approval
                updates.estatus = 'pendiente'; // Ready for payment
                updates.estatusAprobacion = 'Aprobado';
                updates.requiresApproval = false;
                updates.approvalLevel = null;
                updates.approvers = [];

                if (isPayroll && invoice.nominaReferenceId) {
                    const nominaRef = doc(db, "nominas_periodos", invoice.nominaReferenceId);
                    await updateDoc(nominaRef, { estatusAprobacion: 'Aprobado' });
                }

                // POST LEDGER ENTRY
                try {
                    if (isPayroll) {
                        let providerAccId = null;
                        const qProv = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId), where("tipo", "==", "PASIVO"));
                        const snapProv = await getDocs(qProv);
                        if (!snapProv.empty) {
                            const match = snapProv.docs.find(d => d.data().nombre.toLowerCase().includes('nómina') || d.data().nombre.toLowerCase().includes('sueldos por pagar'));
                            providerAccId = match ? match.id : snapProv.docs[0].id;
                        }

                        if (providerAccId && invoice.cuentaId) {
                            await postLedgerEntry(userData.condominioId, {
                                fecha: Timestamp.now(),
                                concepto: `Provisión Nómina: ${invoice.descripcion}`,
                                referenciaId: invoice.id,
                                tipoReferencia: 'NOMINA',
                                detalles: [
                                    { cuentaId: invoice.cuentaId, debe: parseFloat(invoice.total || invoice.monto), haber: 0 },
                                    { cuentaId: providerAccId, debe: 0, haber: parseFloat(invoice.total || invoice.monto) }
                                ]
                            });
                        }
                    } else {
                        // Standard Gasto Ledger Lookup
                        let providerAccId = null;
                        const qProv = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId), where("tipo", "==", "PASIVO"));
                        const snapProv = await getDocs(qProv);
                        if (!snapProv.empty) {
                            const match = snapProv.docs.find(d => d.data().codigo === '2101' || d.data().nombre.includes('Proveedores') || d.data().nombre.includes('Cuentas por Pagar'));
                            providerAccId = match ? match.id : snapProv.docs[0].id; // Fallback
                        }

                        if (providerAccId) {
                            const qAccs = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId));
                            const snapAccs = await getDocs(qAccs);
                            const condoAccounts = snapAccs.docs.map(d => ({ id: d.id, ...d.data() }));

                            const detalles = [];
                            let totalDebit = 0;

                            // 1. Process distribution if present
                            if (invoice.distribucion && invoice.distribucion.length > 0) {
                                for (const row of invoice.distribucion) {
                                    const subAcc = condoAccounts.find(a => a.codigo === row.cuentaCodigo || a.numero === row.cuentaCodigo);
                                    if (subAcc) {
                                        detalles.push({ cuentaId: subAcc.id, debe: parseFloat(row.subtotal), haber: 0 });
                                        totalDebit += parseFloat(row.subtotal);
                                    }
                                }
                            } else {
                                // Fallback to single account
                                const singleAcc = condoAccounts.find(a => a.id === invoice.cuentaId || a.codigo === invoice.cuentaCodigo || a.numero === invoice.cuentaCodigo);
                                if (singleAcc) {
                                    const mainSubtotal = parseFloat(invoice.subtotal || (parseFloat(invoice.monto || invoice.total) - parseFloat(invoice.iva || 0)));
                                    detalles.push({ cuentaId: singleAcc.id, debe: mainSubtotal, haber: 0 });
                                    totalDebit += mainSubtotal;
                                }
                            }

                            // 2. Process IVA if present
                            const ivaAmount = parseFloat(invoice.iva || 0);
                            if (ivaAmount > 0) {
                                let ivaAcc = condoAccounts.find(a => (a.codigo === '1104' || a.numero === '1104' || a.codigo === '1106' || a.numero === '1106'));
                                if (!ivaAcc) {
                                    ivaAcc = condoAccounts.find(a => a.nombre.toLowerCase().includes('iva acreditable') || a.nombre.toLowerCase().includes('iva por acreditar'));
                                }
                                if (ivaAcc) {
                                    detalles.push({ cuentaId: ivaAcc.id, debe: ivaAmount, haber: 0 });
                                    totalDebit += ivaAmount;
                                }
                            }

                            // 3. Process Credit (Accounts Payable / Proveedores)
                            detalles.push({ cuentaId: providerAccId, debe: 0, haber: totalDebit });

                            await postLedgerEntry(userData.condominioId, {
                                fecha: invoice.fecha || Timestamp.now(),
                                concepto: `Provisión Factura: ${invoice.proveedor || invoice.proveedorNombre} - ${invoice.descripcion}`,
                                referenciaId: invoice.id,
                                tipoReferencia: 'GASTO',
                                detalles: detalles
                            });
                        }
                    }
                } catch (e) {
                    console.error("Ledger Error:", e);
                }

                setSuccessNotification({
                    title: 'Factura Aprobada',
                    message: isPayroll ? "Nómina aprobada y lista para Pago/Timbrado." : "Factura aprobada totalmente y contabilizada."
                });
            }

            await updateDoc(invoiceRef, updates);

        } catch (error) {
            console.error(error);
            setErrorNotification("Error al aprobar: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async (invoice) => {
        // 1. Permission Check (Same)
        const canApprove = userData.rol === 'superuser' || userData.rol === 'masteruser' || (invoice.approvers && invoice.approvers.includes(userData.uid)) || (invoice.proveedorId === 'NOMINA_INTERNA' && (userData.rol === 'superuser' || userData.rol === 'masteruser')) || userData.rol === 'comite';
        if (!canApprove) {
            alert("No tienes permiso para rechazar este documento.");
            return;
        }

        const reason = prompt("Describe el motivo del rechazo:");
        if (!reason) return;

        setLoading(true);
        try {
            const invoiceRef = doc(db, "gastos", invoice.id);
            const updates = {
                estatus: invoice.proveedorId === 'NOMINA_INTERNA' ? 'rechazado' : 'rechazado',
                estatusAprobacion: invoice.proveedorId === 'NOMINA_INTERNA' ? 'Rechazado' : undefined,
                approvalHistory: arrayUnion({
                    action: 'rejected',
                    by: userData.uid,
                    userName: userData.nombre,
                    date: Timestamp.now(),
                    reason: reason,
                    level: invoice.approvalLevel || 0
                })
            };

            await updateDoc(invoiceRef, updates);

            if (invoice.proveedorId === 'NOMINA_INTERNA' && invoice.nominaReferenceId) {
                const nominaRef = doc(db, "nominas_periodos", invoice.nominaReferenceId);
                await updateDoc(nominaRef, { estatusAprobacion: 'Rechazado' });
            }

            alert(invoice.proveedorId === 'NOMINA_INTERNA' ? "Nómina rechazada." : "Factura rechazada.");
        } catch (error) {
            console.error(error);
            alert("Error: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTimbrarDispersar = async (factura) => {
        const montoFormateado = Number(factura.monto || factura.total).toFixed(2);
        if (!window.confirm(`¿Estás seguro de Timbrar en el SAT y Dispersar el pago de $${montoFormateado} a la cuenta CLABE del empleado? Esta acción es irreversible y debitará los fondos.`)) {
            return;
        }

        setLoading(true);
        try {
            const functions = getFunctions();
            // Specify 'us-central1' if needed, generally default is fine
            const procesarPagoNomina = httpsCallable(functions, 'procesarPagoNomina');
            const result = await procesarPagoNomina({ gastoId: factura.id });

            if (result.data.success) {
                alert('¡Nómina Timbrada y Dispersada Exitosamente!');
            } else {
                alert('Ocurrió un error (Semi-Procesado): ' + JSON.stringify(result.data));
            }
        } catch (error) {
            console.error(error);
            alert("Error al procesar: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDraft = (e, draftId) => {
        e.stopPropagation();
        setDeleteConfirmId(draftId);
    };

    const handleAutoScan = async (bypassConfirm = false) => {
        const source = processingInvoice.file || processingInvoice.previewUrl;
        if (!source) return;

        // If user already filled data, warn them
        if (!bypassConfirm && (processingInvoice.params.monto || processingInvoice.params.proveedor)) {
            setShowScanConfirm(true);
            return;
        }

        setLoading(true);
        try {
            const extracted = await scanInvoice(source);

            // INTELLIGENCE: Predict Account based on Provider History
            let predictedAccount = '';

            if (extracted.proveedor) {
                // Find last invoice from this provider (Fuzzy Match)
                try {
                    // Fetch last 300 expenses (unsorted to avoid index requirement)
                    // Increased limit to ensure we catch recent items even without server-side sort
                    const q = query(
                        collection(db, "gastos"),
                        where("condominioId", "==", userData.condominioId),
                        limit(300)
                    );
                    const querySnapshot = await getDocs(q);

                    // Sort client-side by registration date (descending)
                    const docs = querySnapshot.docs.map(d => d.data()).sort((a, b) => {
                        const dateA = a.fechaRegistro?.toMillis() || a.fecha?.toMillis() || 0;
                        const dateB = b.fechaRegistro?.toMillis() || b.fecha?.toMillis() || 0;
                        return dateB - dateA;
                    });

                    // Helper: Normalize known aliases
                    const providerAliases = {
                        'cfe': ['cfe', 'comision federal', 'comisión federal'],
                        'telmex': ['telmex', 'telefonos de mexico', 'teléfonos de méxico'],
                        'siapa': ['siapa', 'agua', 'sistema intermunicipal'],
                        'izzi': ['izzi', 'cablevisión', 'cablevision']
                    };

                    const normalize = (str) => str ? str.toLowerCase().replace(/\./g, '') : '';
                    let cleanProvider = normalize(extracted.proveedor);

                    // Expand search terms based on alias
                    let searchTerms = [cleanProvider];
                    // eslint-disable-next-line no-unused-vars
                    for (const [key, aliases] of Object.entries(providerAliases)) {
                        if (aliases.some(alias => cleanProvider.includes(alias))) {
                            searchTerms = aliases; // Use known aliases to search
                            break;
                        }
                    }

                    // Find first match where provider names overlap or match any alias
                    const lastInvoice = docs.find(data => {
                        const historyProv = normalize(data.proveedor);
                        if (!historyProv) return false;

                        // Check if history provider matches ANY of the search terms
                        return searchTerms.some(term => historyProv.includes(term) || term.includes(historyProv));
                    });

                    if (lastInvoice && lastInvoice.cuentaCodigo) {
                        predictedAccount = lastInvoice.cuentaCodigo;
                        console.log("Predicted Account found via fuzzy match:", predictedAccount);

                        const acc = expenseAccounts.find(a => a.codigo === predictedAccount);
                        if (acc) setAccountSearchTerm(`${acc.codigo} - ${acc.nombre}`);
                    }
                } catch (historyError) {
                    console.warn("Could not fetch provider history:", historyError);
                }
            }

            // SMART DESCRIPTION GENERATION
            let smartDesc = `Documento cargado: ${processingInvoice.file?.name || 'recibo.pdf'}`;
            if (extracted.proveedor) {
                let serviceType = 'Gasto de';
                if (extracted.proveedor === 'CFE') serviceType = 'Consumo de Energía Eléctrica';
                else if (extracted.proveedor === 'Telmex' || extracted.proveedor === 'Izzi') serviceType = 'Servicio de Internet/Teléfono';
                else if (extracted.proveedor === 'SIAPA' || extracted.proveedor.includes('Agua')) serviceType = 'Consumo de Agua Potable';

                smartDesc = `${serviceType} ${extracted.proveedor}`;
                if (extracted.periodo) {
                    smartDesc += ` (${extracted.periodo})`;
                }
            }

            // Sync account search term
            let accountNameStr = '';
            if (predictedAccount) {
                const acc = expenseAccounts.find(a => a.codigo === predictedAccount);
                if (acc) {
                    accountNameStr = `${acc.codigo} - ${acc.nombre}`;
                    setAccountSearchTerm(accountNameStr);
                }
            }

            const prevSubtotal = processingInvoice?.params?.subtotal || '';
            const prevCuentaCodigo = processingInvoice?.params?.cuentaCodigo || '';

            const scanSubtotal = extracted.subtotal ? extracted.subtotal.toString() : (extracted.monto ? extracted.monto.toString() : prevSubtotal);
            const scanIva = extracted.iva ? extracted.iva.toString() : '0';

            const defaultDist = [
                {
                    cuentaCodigo: predictedAccount || prevCuentaCodigo || '',
                    subtotal: scanSubtotal,
                    searchTerm: accountNameStr
                }
            ];

            setProcessingInvoice(prev => ({
                ...prev,
                params: {
                    ...prev.params,
                    monto: extracted.monto ? extracted.monto.toString() : prev.params.monto,
                    subtotal: scanSubtotal,
                    iva: scanIva,
                    fecha: extracted.fecha ? moment(extracted.fecha).format('YYYY-MM-DD') : prev.params.fecha,
                    proveedor: extracted.proveedor || prev.params.proveedor,
                    cuentaCodigo: predictedAccount || prev.params.cuentaCodigo,
                    descripcion: smartDesc,
                    distribucion: defaultDist
                }
            }));

            let msg = `Encontrado:\nSubtotal: $${scanSubtotal}\nIVA: $${scanIva}\nTotal: $${extracted.monto || '?'}`;
            if (predictedAccount) {
                const acc = expenseAccounts.find(a => a.codigo === predictedAccount);
                msg += `\n\nCuenta sugerida: ${predictedAccount}${acc ? ` (${acc.nombre})` : ''}`;
            }

            setSuccessNotification({
                title: 'Escaneo Completado',
                message: msg
            });

        } catch (error) {
            console.error(error);
            setErrorNotification("Error OCR: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubtotalChange = (val) => {
        setProcessingInvoice(prev => {
            const sub = parseFloat(val) || 0;
            const iva = parseFloat(prev?.params?.iva || 0);
            return {
                ...prev,
                params: {
                    ...prev.params,
                    subtotal: val,
                    monto: (sub + iva).toFixed(2)
                }
            };
        });
    };

    const handleIvaChange = (val) => {
        setProcessingInvoice(prev => {
            const sub = parseFloat(prev?.params?.subtotal || 0);
            const iva = parseFloat(val) || 0;
            return {
                ...prev,
                params: {
                    ...prev.params,
                    iva: val,
                    monto: (sub + iva).toFixed(2)
                }
            };
        });
    };

    const calculateTotalSubtotal = (dist) => {
        return dist.reduce((acc, item) => acc + (parseFloat(item.subtotal) || 0), 0);
    };

    const handleAddSplitLine = () => {
        setProcessingInvoice(prev => {
            const newDist = [...(prev?.params?.distribucion || [])];
            newDist.push({ cuentaCodigo: '', subtotal: '', searchTerm: '', showSuggestions: false });
            return {
                ...prev,
                params: {
                    ...prev.params,
                    distribucion: newDist
                }
            };
        });
    };

    const handleRemoveSplitLine = (idx) => {
        setProcessingInvoice(prev => {
            const newDist = (prev?.params?.distribucion || []).filter((_, i) => i !== idx);
            const totalSub = calculateTotalSubtotal(newDist);
            const iva = parseFloat(prev?.params?.iva || 0);
            
            // Sync main account with first line
            const firstCode = newDist[0]?.cuentaCodigo || '';
            const mainAcc = expenseAccounts.find(a => a.codigo === firstCode);
            if (idx === 0) {
                setAccountSearchTerm(mainAcc ? `${mainAcc.codigo} - ${mainAcc.nombre}` : '');
            }

            return {
                ...prev,
                params: {
                    ...prev.params,
                    distribucion: newDist,
                    cuentaCodigo: firstCode,
                    subtotal: totalSub.toFixed(2),
                    monto: (totalSub + iva).toFixed(2)
                }
            };
        });
    };

    const handleSplitSubtotalChange = (idx, val) => {
        setProcessingInvoice(prev => {
            const newDist = [...(prev?.params?.distribucion || [])];
            newDist[idx] = { ...newDist[idx], subtotal: val };
            const totalSub = calculateTotalSubtotal(newDist);
            const iva = parseFloat(prev?.params?.iva || 0);
            return {
                ...prev,
                params: {
                    ...prev.params,
                    distribucion: newDist,
                    subtotal: totalSub.toFixed(2),
                    monto: (totalSub + iva).toFixed(2)
                }
            };
        });
    };

    const handleSplitSearchChange = (idx, val) => {
        setProcessingInvoice(prev => {
            const newDist = [...(prev?.params?.distribucion || [])];
            newDist[idx] = { 
                ...newDist[idx], 
                searchTerm: val, 
                showSuggestions: true,
                cuentaCodigo: val === '' ? '' : newDist[idx].cuentaCodigo
            };
            return {
                ...prev,
                params: { ...prev.params, distribucion: newDist }
            };
        });
    };

    const handleSelectSplitAccount = (idx, account) => {
        setProcessingInvoice(prev => {
            const newDist = [...(prev?.params?.distribucion || [])];
            newDist[idx] = { 
                ...newDist[idx], 
                cuentaCodigo: account.codigo, 
                searchTerm: `${account.codigo} - ${account.nombre}`,
                showSuggestions: false 
            };
            
            // Sync first item with main account just in case they have a single item
            const firstCode = newDist[0]?.cuentaCodigo || '';
            if (idx === 0) {
                setAccountSearchTerm(`${account.codigo} - ${account.nombre}`);
            }

            return {
                ...prev,
                params: { 
                    ...prev.params, 
                    distribucion: newDist,
                    cuentaCodigo: firstCode
                }
            };
        });
    };

    const renderProcessingView = () => (
        <div className="split-view-container" style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 180px)' }}>
            {/* Left: Document Viewer */}
            <div className="doc-viewer-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div className="processing-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ color: '#6b7280', fontSize: '0.9em' }}>
                        Vista Previa del Documento
                    </div>
                    <button
                        onClick={handleAutoScan}
                        className="action-btn-primary"
                        style={{ fontSize: '0.9em', padding: '6px 12px' }}
                        title="Intentar leer datos automáticamente"
                    >
                        <IoSparklesOutline /> Auto-Escanear
                    </button>
                </div>
                <div style={{ flex: 1, background: '#374151', borderRadius: '12px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {processingInvoice?.previewUrl ? (
                        (processingInvoice.file?.type?.includes('pdf') || processingInvoice.previewUrl.toLowerCase().includes('.pdf')) ? (
                            <iframe src={`${processingInvoice.previewUrl}#toolbar=1&navpanes=0&view=FitH`} style={{ width: '100%', height: '100%', border: 'none' }} title="PDF Preview" />
                        ) : (
                            <img src={processingInvoice.previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        )
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            <IoDocumentTextOutline size={64} style={{ marginBottom: '16px', opacity: 0.8 }} />
                            <h3 style={{ color: 'white', marginBottom: '8px' }}>Vista Previa</h3>
                            <p style={{ fontSize: '0.9em' }}>Aquí se mostrará el documento PDF o Imagen</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Right: Processing Form */}
            <div className="processing-form-panel" style={{ width: '400px', background: 'white', borderRadius: '12px', padding: '20px', overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #eee', paddingBottom: '16px' }}>
                    <h3 style={{ margin: 0, color: '#111827' }}>Detalles de la Factura</h3>
                    <button
                        onClick={() => {
                            setViewMode('list');
                            setProcessingInvoice(null);
                        }}
                        style={{ background: '#f3f4f6', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', color: '#4b5563', fontWeight: '500' }}
                    >
                        Cerrar / Cancelar
                    </button>
                </div>

                <div className="form-layout" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div className="form-group">
                        <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>Proveedor Detectado</label>
                        <div style={{ position: 'relative', display: 'flex', gap: '10px', flex: 1 }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <input
                                    className="form-input"
                                    placeholder="Escribe o busca proveedor..."
                                    value={processingInvoice?.params?.proveedor || ''}
                                    onChange={(e) => {
                                        setProcessingInvoice(prev => ({ ...prev, params: { ...prev.params, proveedor: e.target.value } }));
                                        setShowProviderSuggestions(true);
                                    }}
                                    onFocus={() => setShowProviderSuggestions(true)}
                                    // Delay hiding to allow click on suggestion
                                    onBlur={() => setTimeout(() => setShowProviderSuggestions(false), 200)}
                                    autoComplete="off"
                                />

                                {/* Suggestions List */}
                                {showProviderSuggestions && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '0 0 8px 8px',
                                        marginTop: '4px',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        zIndex: 50,
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        {proveedores
                                            .filter(p => !processingInvoice?.params?.proveedor || p.nombre.toLowerCase().includes(processingInvoice.params.proveedor.toLowerCase()))
                                            .map(p => (
                                                <div
                                                    key={p.id}
                                                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f9fafb' }}
                                                    onMouseDown={() => {
                                                        setProcessingInvoice(prev => ({ ...prev, params: { ...prev.params, proveedor: p.nombre } }));
                                                        setShowProviderSuggestions(false);
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                                    onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                                >
                                                    {p.nombre}
                                                </div>
                                            ))}
                                        {proveedores.length === 0 && <div style={{ padding: '8px', color: '#9ca3af', fontSize: '0.9em' }}>No hay proveedores registrados.</div>}
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => setShowNewProvider(true)}
                                className="icon-btn"
                                style={{ background: '#eff6ff', color: '#2563eb', width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '6px' }}
                                title="Crear Nuevo Proveedor"
                            >
                                <IoPersonAddOutline size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Dynamic concept splits (Distribución por Cuentas) */}
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <label className="form-label" style={{ fontWeight: '700', color: '#374151', margin: 0 }}>
                                Conceptos y Distribución de Gasto
                            </label>
                            <button
                                type="button"
                                onClick={handleAddSplitLine}
                                style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #dbeafe', padding: '4px 8px', borderRadius: '6px', fontSize: '0.82em', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                + Agregar Cuenta
                            </button>
                        </div>

                        {(processingInvoice?.params?.distribucion || []).map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                                <div style={{ flex: 2, position: 'relative' }}>
                                    <label style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700', display: 'block', marginBottom: '4px' }}>Cuenta Contable</label>
                                    <input
                                        className="form-input"
                                        style={{ padding: '6px 10px', fontSize: '0.88em' }}
                                        placeholder="Buscar cuenta..."
                                        value={item.searchTerm || ''}
                                        onChange={(e) => handleSplitSearchChange(idx, e.target.value)}
                                        onFocus={() => {
                                            setProcessingInvoice(prev => {
                                                const d = [...(prev?.params?.distribucion || [])];
                                                d[idx] = { ...d[idx], showSuggestions: true };
                                                return { ...prev, params: { ...prev.params, distribucion: d } };
                                            });
                                        }}
                                        onBlur={() => setTimeout(() => {
                                            setProcessingInvoice(prev => {
                                                const d = [...(prev?.params?.distribucion || [])];
                                                if (d[idx]) d[idx] = { ...d[idx], showSuggestions: false };
                                                return { ...prev, params: { ...prev.params, distribucion: d } };
                                            });
                                        }, 200)}
                                        autoComplete="off"
                                    />

                                    {item.showSuggestions && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            background: 'white',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '0 0 8px 8px',
                                            marginTop: '4px',
                                            maxHeight: '180px',
                                            overflowY: 'auto',
                                            zIndex: 60 + idx,
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            {expenseAccounts
                                                .filter(a => {
                                                    const s = (item.searchTerm || '').toLowerCase();
                                                    return !s || a.nombre.toLowerCase().includes(s) || a.codigo.toString().includes(s);
                                                })
                                                .map(a => (
                                                    <div
                                                        key={a.codigo}
                                                        style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.85em' }}
                                                        onMouseDown={() => handleSelectSplitAccount(idx, a)}
                                                        onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                                    >
                                                        <strong>{a.codigo}</strong> - {a.nombre}
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700', display: 'block', marginBottom: '4px' }}>Monto</label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        style={{ padding: '6px 10px', fontSize: '0.88em', fontWeight: '600' }}
                                        placeholder="$0.00"
                                        value={item.subtotal || ''}
                                        onChange={(e) => handleSplitSubtotalChange(idx, e.target.value)}
                                    />
                                </div>

                                {((processingInvoice?.params?.distribucion || []).length > 1) && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSplitLine(idx)}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', marginTop: '22px', cursor: 'pointer', padding: '4px' }}
                                        title="Eliminar línea"
                                    >
                                        <IoTrashOutline size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>Fecha</label>
                            <input
                                type="date"
                                className="form-input"
                                value={processingInvoice?.params?.fecha || ''}
                                onChange={(e) => setProcessingInvoice(prev => ({ ...prev, params: { ...prev.params, fecha: e.target.value } }))}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>IVA (Impuestos)</label>
                            <input
                                type="number"
                                className="form-input"
                                placeholder="$0.00"
                                value={processingInvoice?.params?.iva || ''}
                                onChange={(e) => handleIvaChange(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>Monto Total</label>
                            <input
                                type="number"
                                className="form-input"
                                placeholder="$0.00"
                                style={{ fontSize: '1.1em', fontWeight: 'bold' }}
                                value={processingInvoice?.params?.monto || ''}
                                onChange={(e) => setProcessingInvoice(prev => ({ ...prev, params: { ...prev.params, monto: e.target.value } }))}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label" style={{ fontWeight: '600', color: '#374151' }}>Concepto / Descripción</label>
                        <textarea
                            className="form-input"
                            rows="3"
                            placeholder="Descripción del gasto..."
                            value={processingInvoice?.params?.descripcion || ''}
                            onChange={(e) => setProcessingInvoice(prev => ({ ...prev, params: { ...prev.params, descripcion: e.target.value } }))}
                        />
                    </div>

                    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                        <button onClick={handleSaveInvoice} className="form-button" style={{ width: '100%', padding: '12px', fontSize: '1rem' }}>
                            <IoCheckmarkCircleOutline style={{ marginRight: '8px' }} size={20} />
                            Registrar Gasto
                        </button>
                    </div>
                </div>
                <IoDocumentTextOutline className="banner-standard-icon-decor" />
            </div>
        </div>
    );

    // eslint-disable-next-line no-unused-vars
    const renderBuzon = () => {
        if (viewMode === 'processing') return renderProcessingView();

        // Separate Drafts
        const drafts = facturas.filter(f => f.estatus === 'borrador');
        const processed = facturas.filter(f => f.estatus !== 'borrador');

        let filtered = processed;
        if (filterStatus === 'pendientes') filtered = processed.filter(f => f.estatus === 'pendiente');
        if (filterStatus === 'pagados') filtered = processed.filter(f => f.estatus === 'pagado');
        // New Filter for Approval Workflow
        if (filterStatus === 'por_aprobar') filtered = processed.filter(f => f.estatus === 'por_aprobar');

        return (
            <div>
                {/* Upload Banner - Hidden for Comite */}
                {userData.rol !== 'comite' && (
                    <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('invoice-upload').click()}
                        style={{
                            border: dragActive ? '2px dashed #3b82f6' : '2px dashed #cbd5e1',
                            borderRadius: '12px',
                            padding: '32px',
                            textAlign: 'center',
                            marginBottom: '30px',
                            cursor: 'pointer',
                            background: dragActive ? '#eff6ff' : '#f8fafc',
                            transition: 'all 0.2s',
                        }}
                        onMouseOver={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                        onMouseOut={(e) => !dragActive && (e.currentTarget.style.borderColor = '#cbd5e1')}
                    >
                        <input
                            id="invoice-upload"
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleChange}
                            accept="image/*,.pdf"
                        />
                        <IoCloudUploadOutline size={48} color={dragActive ? '#3b82f6' : '#64748b'} />
                        <h4 style={{ margin: '12px 0 4px', color: '#334155' }}>
                            {dragActive ? '¡Suelta los archivos aquí!' : 'Subir Facturas (Masivo) o Fotos'}
                        </h4>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9em' }}>Arrastra múltiples archivos para procesarlos después</p>
                    </div>
                )}

                {/* DRAFTS SECTION */}
                {drafts.length > 0 && (
                    <div style={{ marginBottom: '30px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', padding: '20px' }}>
                        <h3 style={{ marginTop: 0, color: '#9a3412', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IoWarningOutline /> Documentos por Procesar ({drafts.length})
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px', marginTop: '15px' }}>
                            {drafts.map(draft => (
                                <div
                                    key={draft.id}
                                    onClick={() => openDraft(draft)}
                                    style={{
                                        background: 'white',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        transition: 'transform 0.1s'
                                    }}
                                    onMouseOver={e => e.currentTarget.style.borderColor = '#fdba74'}
                                    onMouseOut={e => e.currentTarget.style.borderColor = '#e5e7eb'}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                        <div style={{ fontSize: '0.9em', color: '#6b7280' }}>
                                            {moment(draft.fechaRegistro?.toDate()).format('D MMM HH:mm')}
                                        </div>
                                        <button
                                            onClick={(e) => handleDeleteDraft(e, draft.id)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0', display: 'flex', alignItems: 'center' }}
                                            title="Eliminar borrador"
                                        >
                                            <IoTrashOutline size={16} />
                                        </button>
                                    </div>

                                    <div style={{ fontWeight: '600', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {draft.descripcion}
                                    </div>
                                    <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#ea580c', fontWeight: '500' }}>
                                        Haz click para procesar
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}



                <div className="table-responsive">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Proveedor</th>
                                <th>Concepto</th>
                                <th>Monto</th>
                                <th>Estatus</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="6" style={{ textAlign: 'center' }}>No hay facturas registradas.</td></tr>
                            ) : filtered.map(item => (
                                <tr key={item.id}>
                                    <td>{moment(item.fecha?.toDate()).format('D MMM YYYY')}</td>
                                    <td style={{ fontWeight: 'bold' }}>{item.proveedor}</td>
                                    <td>{item.descripcion}</td>
                                    <td>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(item.monto)}</td>
                                    <td>
                                        <span
                                            className={`status-badge ${item.estatus}`}
                                            style={item.estatus === 'por_aprobar' ? { background: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74' } : {}}
                                        >
                                            {item.estatus === 'por_aprobar' ? 'Por Aprobar' : (item.estatus === 'pendiente' ? 'Por Pagar' : item.estatus)}
                                        </span>
                                    </td>
                                    <td>
                                        {/* Acciones simplificadas */}
                                        {/* Acciones simplificadas */}
                                        {userData.rol !== 'comite' && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setEditingExpense(item);
                                                        setShowRegistrarGasto(true);
                                                    }}
                                                    className="icon-btn"
                                                    style={{ color: '#2563eb', marginRight: '8px' }}
                                                    title="Editar detalle"
                                                >
                                                    <IoCreateOutline />
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        if (window.confirm(`¿Estás seguro de eliminar esta factura de ${item.proveedor}? Si ya estaba pagada, verifica los saldos logicos.`)) {
                                                            try {
                                                                await deleteDoc(doc(db, "gastos", item.id));

                                                                // Delete linked Ledger Entries (Asientos Contables) to clean finance reports
                                                                const qLedger = query(collection(db, "contabilidad_asientos"), where("referenciaId", "==", item.id));
                                                                const ledgerSnap = await getDocs(qLedger);
                                                                ledgerSnap.forEach(async (d) => {
                                                                    await deleteDoc(d.ref);
                                                                });

                                                                // Optional: If linked to bank movement, we should probably unlink it? 
                                                                // User asked to "borrar ese registro en el buzon".
                                                                // Ideally we check if it has a movimientoBancarioId and revert its status.
                                                                if (item.movimientoBancarioId) {
                                                                    const movRef = doc(db, "movimientos_bancarios", item.movimientoBancarioId);
                                                                    // Revert bank movement to unclassified? Or just unlink?
                                                                    // Safer to just unlink for now to avoid data loss on bank side
                                                                    await updateDoc(movRef, {
                                                                        estatus: 'pendiente', // Re-opens for classification
                                                                        gastoId: null,
                                                                        tipoClasificacion: null,
                                                                        categoria: null
                                                                    });
                                                                }
                                                            } catch (e) {
                                                                console.error(e);
                                                                alert("Error al eliminar");
                                                            }
                                                        }
                                                    }}
                                                    className="icon-btn"
                                                    style={{ color: '#dc2626' }}
                                                    title="Eliminar registro"
                                                >
                                                    <IoTrash />
                                                </button>
                                            </>
                                        )}

                                        {/* Approval Actions */}
                                        {/* Approval Actions - Show ONLY if user is authorized approver */}
                                        {item.estatus === 'por_aprobar' && (userData.rol === 'superuser' || userData.rol === 'masteruser' || item.approvers?.includes(userData.uid)) && (
                                            <>
                                                <div style={{ width: '1px', height: '20px', background: '#ddd', margin: '0 8px' }}></div>
                                                <button
                                                    onClick={() => handleApprove(item)}
                                                    className="icon-btn"
                                                    style={{ color: '#059669' }}
                                                    title="Aprobar"
                                                >
                                                    <IoCheckmarkCircleOutline size={22} />
                                                </button>
                                                <button
                                                    onClick={() => handleReject(item)}
                                                    className="icon-btn"
                                                    style={{ color: '#dc2626' }}
                                                    title="Rechazar"
                                                >
                                                    <IoCloseCircleOutline size={22} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div >
        );
    };


    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/gestion-facturas" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                
                <div className="ks-scroll-area">
                    <div className="facturas-container">
                        {/* ── STITCH HERO BANNER (flows with content) ── */}
                        <div className="muro-header-stitch">
                            <div className="muro-hero-card">
                                <div className="muro-hero-content">
                                    <div className="muro-hero-text">
                                        <h1>
                                            {viewContext === 'nominas' ? 'Gestión de Nóminas' : 'Gestión de Facturas'}
                                        </h1>
                                        <p>
                                            {viewContext === 'nominas' ? 'Administración y timbrado de periodos de nómina.' : 'Control de comprobantes, proveedores y aprobaciones de gasto.'}
                                        </p>
                                    </div>
                                    
                                    <div className="muro-hero-actions">
                                        {userData.rol !== 'comite' && viewContext !== 'nominas' && (
                                            <>
                                                <button className="muro-hero-action-btn" onClick={() => document.getElementById('file-upload').click()} title="Subir XML/PDF">
                                                    <IoCloudUploadOutline size={20} /> <span>Subir</span>
                                                </button>
                                                <button className="muro-hero-action-btn" onClick={() => {
                                                    setEditingExpense(null);
                                                    setShowRegistrarGasto(true);
                                                }} title="Registrar Gasto">
                                                    <IoAdd size={20} /> <span>Registrar</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <IoReceiptOutline className="muro-hero-icon-decor" />
                            </div>
                        </div>

                        {/* Hidden Input for generic click upload */}
                        <input
                            type="file"
                            id="file-upload"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleChange}
                        />

                        {/* View Mode: Processing vs List */}
                        {viewMode === 'processing' && processingInvoice ? (
                            renderProcessingView()
                        ) : (
                            <>
                                {/* Tabs */}
                                <div className="facturas-tabs">
                                    {viewContext !== 'nominas' && (
                                        <>
                                            <button
                                                className={`tab-btn ${activeTab === 'buzon' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('buzon')}
                                            >
                                                Buzón de Facturas
                                            </button>
                                            <button
                                                className={`tab-btn ${activeTab === 'proveedores' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('proveedores')}
                                            >
                                                Directorio Proveedores
                                            </button>
                                        </>
                                    )}

                                    {viewContext !== 'gastos' && (
                                        <button
                                            className={`tab-btn ${activeTab === 'nominas' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('nominas')}
                                        >
                                            <IoPersonAddOutline style={{ marginRight: 6, verticalAlign: 'text-bottom' }} /> Nóminas
                                        </button>
                                    )}
                                </div>

                                <div className="facturas-card" onDragEnter={handleDrag} style={{ marginBottom: '100px' }}>

                                    {/* Drag Overlay */}
                                    {dragActive && (
                                        <div className="drag-active-overlay" onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                                            <div className="drag-content">
                                                <IoCloudUploadOutline size={80} color="white" />
                                                <h2>Suelta los archivos aquí</h2>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'buzon' && (
                                        <>
                                            {/* Drag & Drop Zone (Small Hint) */}
                                            {!loading && facturas.length === 0 && (
                                                <div
                                                    className={`drag-drop-zone ${dragActive ? 'active' : ''}`}
                                                    onClick={() => document.getElementById('file-upload').click()}
                                                >
                                                    <div className="drag-drop-content">
                                                        <IoCloudUploadOutline className="upload-icon-large" />
                                                        <h3>Arrastra facturas aquí o haz clic para subir</h3>
                                                        <p>Soporta PDF, XML, JPG, PNG</p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Toolbar */}
                                            <div className="table-toolbar">
                                                <div className="filter-group">
                                                    <div
                                                        className={`filter-chip ${filterStatus === 'todos' ? 'active' : ''}`}
                                                        onClick={() => setFilterStatus('todos')}
                                                    >
                                                        Todos
                                                    </div>
                                                    <div
                                                        className={`filter-chip ${filterStatus === 'borrador' ? 'active' : ''}`}
                                                        onClick={() => setFilterStatus('borrador')}
                                                    >
                                                        Por Procesar
                                                    </div>
                                                    <div
                                                        className={`filter-chip ${filterStatus === 'por_aprobar' ? 'active' : ''}`}
                                                        onClick={() => setFilterStatus('por_aprobar')}
                                                    >
                                                        Por Aprobar
                                                    </div>
                                                    <div
                                                        className={`filter-chip ${filterStatus === 'pendiente' ? 'active' : ''}`}
                                                        onClick={() => setFilterStatus('pendiente')}
                                                    >
                                                        Por Pagar
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                                    {facturas.length} documentos
                                                </div>
                                            </div>

                                            {/* Table / Mobile List */}
                                            {isMobile ? (
                                                <div className="mobile-facturas-list">
                                                    {facturas
                                                        .filter(f => f.proveedorId !== 'NOMINA_INTERNA') // Ocultar nóminas del buzón de facturas general
                                                        .filter(f => filterStatus === 'todos' ? true : f.estatus === filterStatus)
                                                        .map((factura) => (
                                                            <div key={factura.id} className="mobile-factura-card">
                                                                <div className="mfc-header">
                                                                    <span className="mfc-proveedor">{factura.proveedor || factura.proveedorNombre || '(Sin Asignar)'}</span>
                                                                    <span className="mfc-date">{factura.fecha ? moment(factura.fecha.toDate()).format('D MMM YYYY') : '-'}</span>
                                                                </div>
                                                                <div className="mfc-body">
                                                                    <div className="mfc-monto">${parseFloat(factura.monto || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                                                    <div className="mfc-desc">{factura.descripcion}</div>
                                                                </div>
                                                                <div className="mfc-footer">
                                                                    <span className={`status-pill ${factura.estatus || 'borrador'}`}>
                                                                        {factura.estatus === 'borrador' ? 'Borrador' :
                                                                            factura.estatus === 'por_aprobar' ? 'Revisión' :
                                                                                factura.estatus === 'pendiente' ? 'Por Pagar' :
                                                                                    factura.estatus}
                                                                    </span>
                                                                    <div className="mfc-actions">
                                                                        {factura.proveedorId === 'NOMINA_INTERNA' ? (
                                                                            <button
                                                                                className="glass-icon-btn success-glass"
                                                                                title="Ver Desglose de Nómina"
                                                                                onClick={() => navigate('/gestion-nomina', { state: { nominaReferenceId: factura.nominaReferenceId } })}
                                                                            >
                                                                                <IoDocumentTextOutline />
                                                                            </button>
                                                                        ) : (
                                                                            <>
                                                                                {factura.estatus === 'borrador' ? (
                                                                                    <button className="glass-icon-btn" title="Procesar" onClick={() => openDraft(factura)}>
                                                                                        <IoCreateOutline />
                                                                                    </button>
                                                                                ) : (
                                                                                    <button className="glass-icon-btn" title="Ver Detalles" onClick={() => openDraft(factura)}>
                                                                                        <IoDocumentTextOutline />
                                                                                    </button>
                                                                                )}
                                                                            </>
                                                                        )}

                                                                        {factura.estatus === 'por_aprobar' && (
                                                                            <>
                                                                                <button className="glass-icon-btn success-glass" title="Aprobar" onClick={() => handleApprove(factura)}>
                                                                                    <IoCheckmarkCircleOutline />
                                                                                </button>
                                                                                <button className="glass-icon-btn danger-glass" title="Rechazar" onClick={() => handleReject(factura)}>
                                                                                    <IoCloseCircleOutline />
                                                                                </button>
                                                                            </>
                                                                        )}

                                                                        {factura.estatusAprobacion === 'Pendiente' && factura.proveedorId === 'NOMINA_INTERNA' && (
                                                                            <>
                                                                                <button className="glass-icon-btn success-glass" title="Aprobar Nómina" onClick={() => handleApprove(factura)}>
                                                                                    <IoCheckmarkCircleOutline />
                                                                                </button>
                                                                                <button className="glass-icon-btn danger-glass" title="Rechazar Nómina" onClick={() => handleReject(factura)}>
                                                                                    <IoCloseCircleOutline />
                                                                                </button>
                                                                            </>
                                                                        )}

                                                                        <button className="glass-icon-btn danger-glass" title="Eliminar" onClick={(e) => handleDeleteDraft(e, factura.id)}>
                                                                            <IoTrashOutline />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    {facturas.filter(f => f.proveedorId !== 'NOMINA_INTERNA').filter(f => filterStatus === 'todos' ? true : f.estatus === filterStatus).length === 0 && (
                                                        <div className="empty-state">
                                                            <IoDocumentTextOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                                                            <p>No hay facturas registradas.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="facturas-table-wrapper">
                                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Proveedor</th>
                                                <th>Concepto / Descripción</th>
                                                <th>Monto</th>
                                                <th>Estatus</th>
                                                <th style={{ textAlign: 'right' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {facturas
                                                .filter(f => f.proveedorId !== 'NOMINA_INTERNA') // Ocultar nóminas del buzón de facturas general
                                                .filter(f => filterStatus === 'todos' ? true : f.estatus === filterStatus)
                                                .map((factura) => (
                                                    <tr key={factura.id}>
                                                        <td>
                                                            {factura.fecha ? moment(factura.fecha.toDate()).format('D MMM YYYY') : '-'}
                                                        </td>
                                                        <td className="col-prov">
                                                            {factura.proveedor || factura.proveedorNombre || '(Sin Asignar)'}
                                                        </td>
                                                        <td className="col-desc" title={factura.descripcion}>
                                                            {factura.descripcion}
                                                        </td>
                                                        <td className="col-monto">
                                                            ${parseFloat(factura.monto || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                        </td>
                                                        <td>
                                                            <span className={`status-pill ${factura.estatus || 'borrador'}`}>
                                                                {factura.estatus === 'borrador' ? 'Borrador' :
                                                                    factura.estatus === 'por_aprobar' ? 'Revisión' :
                                                                        factura.estatus === 'pendiente' ? 'Por Pagar' :
                                                                            factura.estatus}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                                                                {factura.proveedorId === 'NOMINA_INTERNA' ? (
                                                                    <button
                                                                        className="glass-icon-btn success-glass"
                                                                        title="Ver Desglose de Nómina"
                                                                        onClick={() => navigate('/gestion-nomina', { state: { nominaReferenceId: factura.nominaReferenceId } })}
                                                                    >
                                                                        <IoDocumentTextOutline />
                                                                    </button>
                                                                ) : (
                                                                    <>
                                                                        {factura.estatus === 'borrador' ? (
                                                                            <button className="glass-icon-btn" title="Procesar" onClick={() => openDraft(factura)}>
                                                                                <IoCreateOutline />
                                                                            </button>
                                                                        ) : (
                                                                            <button className="glass-icon-btn" title="Ver Detalles" onClick={() => openDraft(factura)}>
                                                                                <IoDocumentTextOutline />
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {factura.estatus === 'por_aprobar' && (
                                                                    <>
                                                                        <button className="glass-icon-btn success-glass" title="Aprobar" onClick={() => handleApprove(factura)}>
                                                                            <IoCheckmarkCircleOutline />
                                                                        </button>
                                                                        <button className="glass-icon-btn danger-glass" title="Rechazar" onClick={() => handleReject(factura)}>
                                                                            <IoCloseCircleOutline />
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {/* Special status for Payroll without approval logic required (Directly Pending Approval globally) */}
                                                                {factura.estatusAprobacion === 'Pendiente' && factura.proveedorId === 'NOMINA_INTERNA' && (
                                                                    <>
                                                                        <button className="glass-icon-btn success-glass" title="Aprobar Nómina" onClick={() => handleApprove(factura)}>
                                                                            <IoCheckmarkCircleOutline />
                                                                        </button>
                                                                        <button className="glass-icon-btn danger-glass" title="Rechazar Nómina" onClick={() => handleReject(factura)}>
                                                                            <IoCloseCircleOutline />
                                                                        </button>
                                                                    </>
                                                                )}

                                                                <button className="glass-icon-btn danger-glass" title="Eliminar" onClick={(e) => handleDeleteDraft(e, factura.id)}>
                                                                    <IoTrashOutline />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            {facturas.length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="empty-state">
                                                        <IoDocumentTextOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                                                        <p>No hay facturas registradas.</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                            </>
                        )}

                        {activeTab === 'nominas' && (
                            <>
                                <div className="table-toolbar">
                                    <div className="filter-group">
                                        <div
                                            className={`filter-chip ${filterStatus === 'todos' ? 'active' : ''}`}
                                            onClick={() => setFilterStatus('todos')}
                                        >
                                            Todos
                                        </div>
                                        <div
                                            className={`filter-chip ${filterStatus === 'borrador' ? 'active' : ''}`}
                                            onClick={() => setFilterStatus('borrador')}
                                        >
                                            Por Procesar
                                        </div>
                                        <div
                                            className={`filter-chip ${filterStatus === 'por_aprobar' ? 'active' : ''}`}
                                            onClick={() => setFilterStatus('por_aprobar')}
                                        >
                                            Por Aprobar
                                        </div>
                                        <div
                                            className={`filter-chip ${filterStatus === 'pendiente' ? 'active' : ''}`}
                                            onClick={() => setFilterStatus('pendiente')}
                                        >
                                            Por Pagar
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                        Nóminas y Desglose
                                    </div>
                                </div>

                                {/* Table / Mobile List */}
                                {isMobile ? (
                                    <div className="mobile-facturas-list">
                                        {[...recordatoriosNomina, ...facturas.filter(f => f.proveedorId === 'NOMINA_INTERNA')]
                                            .filter(f => filterStatus === 'todos' ? true : f.estatus === filterStatus)
                                            .sort((a, b) => b.fecha.toDate() - a.fecha.toDate())
                                            .map((factura) => (
                                                <div key={factura.id} className="mobile-factura-card" style={factura.isRecordatorio ? { backgroundColor: '#fdfcbc' } : {}}>
                                                    <div className="mfc-header">
                                                        <span className="mfc-proveedor">{factura.proveedor || factura.proveedorNombre || '(Sin Asignar)'}</span>
                                                        <span className="mfc-date">{factura.fecha ? moment(factura.fecha.toDate()).format('D MMM YYYY') : '-'}</span>
                                                    </div>
                                                    <div className="mfc-body">
                                                        <div className="mfc-monto">${parseFloat(factura.monto || factura.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                        <div className="mfc-desc">
                                                            {factura.descripcion}
                                                            {factura.isRecordatorio && <span style={{ fontSize: '0.75rem', display: 'block', color: '#eab308' }}>¡Periodo pendiente!</span>}
                                                        </div>
                                                    </div>
                                                    <div className="mfc-footer">
                                                        <span className={`status-pill ${factura.estatus || 'borrador'}`}>
                                                            {factura.estatus === 'borrador' ? 'Por Calcular' :
                                                                factura.estatus === 'por_aprobar' ? 'En Revisión (Comité)' :
                                                                    factura.estatus === 'pendiente' ? 'Aprobada / Por Pagar' :
                                                                        factura.estatus}
                                                        </span>
                                                        <div className="mfc-actions">
                                                            {factura.isRecordatorio ? (
                                                                <button
                                                                    className="action-btn-icon"
                                                                    style={{ color: '#d97706', background: '#fef3c7', borderColor: '#d97706' }}
                                                                    title="Calcular Nómina Ahora"
                                                                    onClick={() => navigate('/gestion-nomina', { state: { preselectEmpleadoId: factura.empleado.id } })}
                                                                >
                                                                    <IoCreateOutline />
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="action-btn-icon"
                                                                        style={{ color: '#059669', background: '#ecfdf5', borderColor: '#059669' }}
                                                                        title="Ver Desglose de Nómina"
                                                                        onClick={() => navigate('/gestion-nomina', { state: { nominaReferenceId: factura.nominaReferenceId } })}
                                                                    >
                                                                        <IoDocumentTextOutline />
                                                                    </button>

                                                                    {factura.estatusAprobacion === 'Pendiente' && (userData.rol === 'superuser' || userData.rol === 'masteruser' || userData.rol === 'comite') && (
                                                                        <>
                                                                            <button className="action-btn-icon success" title="Aprobar Nómina" onClick={() => handleApprove(factura)}>
                                                                                <IoCheckmarkCircleOutline color="green" />
                                                                            </button>
                                                                            <button className="action-btn-icon danger" title="Rechazar Nómina" onClick={() => handleReject(factura)}>
                                                                                <IoCloseCircleOutline color="red" />
                                                                            </button>
                                                                        </>
                                                                    )}

                                                                    {factura.estatus === 'pendiente' && (
                                                                        <button
                                                                            className="action-btn-icon"
                                                                            style={{ color: '#6366f1', background: '#e0e7ff', borderColor: '#6366f1' }}
                                                                            title="Timbrar en SAT y Dispersar Pago (Stripe)"
                                                                            onClick={() => handleTimbrarDispersar(factura)}
                                                                        >
                                                                            <IoFlashOutline />
                                                                        </button>
                                                                    )}

                                                                    {(factura.estatus === 'pagado' || factura.estatus === 'Pagado') && factura.facturapiReceiptId && (
                                                                        <>
                                                                            <button
                                                                                className="action-btn-icon"
                                                                                style={{ color: '#0284c7', background: '#f0f9ff', borderColor: '#0284c7' }}
                                                                                title="Descargar PDF del Recibo (SAT)"
                                                                                onClick={(e) => { e.stopPropagation(); openAuthenticatedInvoice(factura.facturapiReceiptId, 'pdf').catch(console.error); }}
                                                                            >
                                                                                <IoCloudDownloadOutline />
                                                                            </button>
                                                                            <button
                                                                                className="action-btn-icon"
                                                                                style={{ color: '#ea580c', background: '#fff7ed', borderColor: '#ea580c' }}
                                                                                title="Descargar XML del Recibo (SAT)"
                                                                                onClick={(e) => { e.stopPropagation(); openAuthenticatedInvoice(factura.facturapiReceiptId, 'xml').catch(console.error); }}
                                                                            >
                                                                                <span style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>XML</span>
                                                                            </button>
                                                                        </>
                                                                    )}

                                                                    {factura.estatus !== 'pagado' && factura.estatus !== 'Pagado' && factura.estatus !== 'Timbrado' && (
                                                                        <button className="action-btn-icon danger" title="Eliminar" onClick={async (e) => {
                                                                            e.stopPropagation();
                                                                            if (window.confirm("¿Seguro que deseas eliminar esta nómina? Si aplicaste incidencias en este recibo, volverán a estar pendientes en su expediente.")) {
                                                                                try {
                                                                                    await deleteDoc(doc(db, "gastos", factura.id));
                                                                                    if (factura.nominaReferenceId) {
                                                                                        await deleteDoc(doc(db, "nominas_periodos", factura.nominaReferenceId));

                                                                                        if (factura.empleado?.id) {
                                                                                            const qInc = query(collection(db, `empleados/${factura.empleado.id}/incidencias`), where('nominaReferenceId', '==', factura.nominaReferenceId));
                                                                                            const snap = await getDocs(qInc);
                                                                                            if (!snap.empty) {
                                                                                                const batchArr = snap.docs.map(d => setDoc(doc(db, `empleados/${factura.empleado.id}/incidencias`, d.id), { estatus: 'Pendiente Aplicación', nominaReferenceId: null }, { merge: true }));
                                                                                                await Promise.all(batchArr);
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                } catch (err) { console.error(err); }
                                                                            }
                                                                        }}>
                                                                            <IoTrashOutline />
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        {[...recordatoriosNomina, ...facturas.filter(f => f.proveedorId === 'NOMINA_INTERNA')].length === 0 && (
                                            <div className="empty-state">
                                                <IoPersonAddOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                                                <p>No hay nóminas registradas ni recordatorios para esta fecha.</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="facturas-table-wrapper">
                                        <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha / Periodo</th>
                                                <th>Empleado</th>
                                                <th>Concepto / Descripción</th>
                                                <th>Importe Neto</th>
                                                <th>Estatus</th>
                                                <th style={{ textAlign: 'right' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...recordatoriosNomina, ...facturas.filter(f => f.proveedorId === 'NOMINA_INTERNA')]
                                                .filter(f => filterStatus === 'todos' ? true : f.estatus === filterStatus)
                                                .sort((a, b) => b.fecha.toDate() - a.fecha.toDate())
                                                .map((factura) => (
                                                    <tr key={factura.id} style={factura.isRecordatorio ? { backgroundColor: '#fdfcbc' } : {}}>
                                                        <td>
                                                            {factura.fecha ? moment(factura.fecha.toDate()).format('D MMM YYYY') : '-'}
                                                        </td>
                                                        <td className="col-prov">
                                                            {factura.proveedor || factura.proveedorNombre || '(Sin Asignar)'}
                                                        </td>
                                                        <td className="col-desc" title={factura.descripcion}>
                                                            {factura.descripcion}
                                                            {factura.isRecordatorio && <span style={{ fontSize: '0.75rem', display: 'block', color: '#eab308' }}>¡Periodo pendiente!</span>}
                                                        </td>
                                                        <td className="col-monto">
                                                            ${parseFloat(factura.monto || factura.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </td>
                                                        <td>
                                                            <span className={`status-pill ${factura.estatus || 'borrador'}`}>
                                                                {factura.estatus === 'borrador' ? 'Por Calcular' :
                                                                    factura.estatus === 'por_aprobar' ? 'En Revisión (Comité)' :
                                                                        factura.estatus === 'pendiente' ? 'Aprobada / Por Pagar' :
                                                                            factura.estatus}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                                                                {factura.isRecordatorio ? (
                                                                    <button
                                                                        className="action-btn-icon"
                                                                        style={{ color: '#d97706', background: '#fef3c7', borderColor: '#d97706' }}
                                                                        title="Calcular Nómina Ahora"
                                                                        onClick={() => navigate('/gestion-nomina', { state: { preselectEmpleadoId: factura.empleado.id } })}
                                                                    >
                                                                        <IoCreateOutline />
                                                                    </button>
                                                                ) : (
                                                                    <>
                                                                        <button
                                                                            className="action-btn-icon"
                                                                            style={{ color: '#059669', background: '#ecfdf5', borderColor: '#059669' }}
                                                                            title="Ver Desglose de Nómina"
                                                                            onClick={() => navigate('/gestion-nomina', { state: { nominaReferenceId: factura.nominaReferenceId } })}
                                                                        >
                                                                            <IoDocumentTextOutline />
                                                                        </button>

                                                                        {factura.estatusAprobacion === 'Pendiente' && (userData.rol === 'superuser' || userData.rol === 'masteruser' || userData.rol === 'comite') && (
                                                                            <>
                                                                                <button className="action-btn-icon success" title="Aprobar Nómina" onClick={() => handleApprove(factura)}>
                                                                                    <IoCheckmarkCircleOutline color="green" />
                                                                                </button>
                                                                                <button className="action-btn-icon danger" title="Rechazar Nómina" onClick={() => handleReject(factura)}>
                                                                                    <IoCloseCircleOutline color="red" />
                                                                                </button>
                                                                            </>
                                                                        )}

                                                                        {factura.estatus === 'pendiente' && (
                                                                            <button
                                                                                className="action-btn-icon"
                                                                                style={{ color: '#6366f1', background: '#e0e7ff', borderColor: '#6366f1' }}
                                                                                title="Timbrar en SAT y Dispersar Pago (Stripe)"
                                                                                onClick={() => handleTimbrarDispersar(factura)}
                                                                            >
                                                                                <IoFlashOutline />
                                                                            </button>
                                                                        )}

                                                                        {(factura.estatus === 'pagado' || factura.estatus === 'Pagado') && factura.facturapiReceiptId && (
                                                                            <>
                                                                                <button
                                                                                    className="action-btn-icon"
                                                                                    style={{ color: '#0284c7', background: '#f0f9ff', borderColor: '#0284c7' }}
                                                                                    title="Descargar PDF del Recibo (SAT)"
                                                                                    onClick={(e) => { e.stopPropagation(); openAuthenticatedInvoice(factura.facturapiReceiptId, 'pdf').catch(console.error); }}
                                                                                >
                                                                                    <IoCloudDownloadOutline />
                                                                                </button>
                                                                                <button
                                                                                    className="action-btn-icon"
                                                                                    style={{ color: '#ea580c', background: '#fff7ed', borderColor: '#ea580c' }}
                                                                                    title="Descargar XML del Recibo (SAT)"
                                                                                    onClick={(e) => { e.stopPropagation(); openAuthenticatedInvoice(factura.facturapiReceiptId, 'xml').catch(console.error); }}
                                                                                >
                                                                                    <span style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>XML</span>
                                                                                </button>
                                                                            </>
                                                                        )}

                                                                        {factura.estatus !== 'pagado' && factura.estatus !== 'Pagado' && factura.estatus !== 'Timbrado' && (
                                                                            <button className="action-btn-icon danger" title="Eliminar" onClick={async (e) => {
                                                                                e.stopPropagation();
                                                                                if (window.confirm("¿Seguro que deseas eliminar esta nómina? Si aplicaste incidencias en este recibo, volverán a estar pendientes en su expediente.")) {
                                                                                    try {
                                                                                        await deleteDoc(doc(db, "gastos", factura.id));
                                                                                        if (factura.nominaReferenceId) {
                                                                                            await deleteDoc(doc(db, "nominas_periodos", factura.nominaReferenceId));

                                                                                            // Revertir incidencias vinculadas a este empleado/nomina
                                                                                            if (factura.empleado?.id) {
                                                                                                const qInc = query(collection(db, `empleados/${factura.empleado.id}/incidencias`), where('nominaReferenceId', '==', factura.nominaReferenceId));
                                                                                                const snap = await getDocs(qInc);
                                                                                                if (!snap.empty) {
                                                                                                    const batchArr = snap.docs.map(d => setDoc(doc(db, `empleados/${factura.empleado.id}/incidencias`, d.id), { estatus: 'Pendiente Aplicación', nominaReferenceId: null }, { merge: true }));
                                                                                                    await Promise.all(batchArr);
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                    } catch (err) { console.error(err); }
                                                                                }
                                                                            }}>
                                                                                <IoTrashOutline />
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            {[...recordatoriosNomina, ...facturas.filter(f => f.proveedorId === 'NOMINA_INTERNA')].length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="empty-state">
                                                        <IoPersonAddOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                                                        <p>No hay nóminas registradas ni recordatorios para esta fecha.</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'proveedores' && (
                            <>
                                <div className="table-toolbar">
                                    <h3>Directorio Interno</h3>
                                    <button className="btn-secondary-action" onClick={() => {
                                        setEditingProvider(null);
                                        setNewProviderName('');
                                        setShowNewProvider(true);
                                    }}>
                                        <IoPersonAddOutline /> Agregar Proveedor
                                    </button>
                                </div>

                                {/* Table / Mobile List */}
                                {isMobile ? (
                                    <div className="mobile-facturas-list">
                                        {proveedores.map((prov) => (
                                            <div key={prov.id} className="mobile-factura-card">
                                                <div className="mfc-header">
                                                    <span className="mfc-proveedor">{prov.nombre}</span>
                                                    <span className="mfc-date">{prov.rfc || 'N/A'}</span>
                                                </div>
                                                <div className="mfc-body">
                                                    <div className="mfc-desc">
                                                        <span style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85em' }}>
                                                            {prov.categoria || 'General'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="mfc-footer">
                                                    <div className="mfc-actions" style={{ marginLeft: 'auto' }}>
                                                        <button className="action-btn-icon" onClick={() => {
                                                            setEditingProvider(prov);
                                                            setNewProviderName(prov.nombre);
                                                            setNewProviderRfc(prov.rfc || '');
                                                            setNewProviderCategory(prov.categoria || '');
                                                            setShowNewProvider(true);
                                                        }}>
                                                            <IoCreateOutline />
                                                        </button>
                                                        <button className="action-btn-icon danger" onClick={() => handleDeleteProvider(prov.id, prov.nombre)}>
                                                            <IoTrashOutline />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {proveedores.length === 0 && (
                                            <div className="empty-state">
                                                <IoPersonAddOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                                                <p>No hay proveedores en el directorio.</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="facturas-table-wrapper">
                                        <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Nombre Comercial</th>
                                                <th>RFC</th>
                                                <th>Categoría</th>
                                                <th style={{ textAlign: 'right' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {proveedores.map((prov) => (
                                                <tr key={prov.id}>
                                                    <td className="col-prov">{prov.nombre}</td>
                                                    <td>{prov.rfc || 'N/A'}</td>
                                                    <td>
                                                        <span style={{ background: '#f3f4f6', padding: '4px 8px', borderRadius: '4px', fontSize: '0.85em' }}>
                                                            {prov.categoria || 'General'}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                            <button className="action-btn-icon" onClick={() => {
                                                                setEditingProvider(prov);
                                                                setNewProviderName(prov.nombre);
                                                                setNewProviderRfc(prov.rfc || '');
                                                                setNewProviderCategory(prov.categoria || '');
                                                                setShowNewProvider(true);
                                                            }}>
                                                                <IoCreateOutline />
                                                            </button>
                                                            <button className="action-btn-icon danger" onClick={() => handleDeleteProvider(prov.id, prov.nombre)}>
                                                                <IoTrashOutline />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}

                    </div>
                </>
            )}

            {/* Modals */}
            {showRegistrarGasto && (
                <RegistrarGastoModal
                    onClose={() => setShowRegistrarGasto(false)}
                    onSave={(data) => {
                        // Adapting to existing logic if needed, or just let the modal handle it separately?
                        // The existing modal likely writes to DB directly.
                        setShowRegistrarGasto(false);
                    }}
                    existingExpense={editingExpense}
                    condominioId={userData.condominioId}
                    userId={userData.uid}
                />
            )}

            {showNewProvider && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h3>
                        <form onSubmit={handleCreateProvider} className="modal-form">
                            <label>Nombre Comercial / Razón Social</label>
                            <input
                                className="form-input"
                                value={newProviderName}
                                onChange={e => setNewProviderName(e.target.value)}
                                required
                            />
                            <label>RFC (Opcional)</label>
                            <input
                                className="form-input"
                                value={newProviderRfc}
                                onChange={e => setNewProviderRfc(e.target.value)}
                            />
                            <label>Categoría</label>
                            <input
                                className="form-input"
                                value={newProviderCategory}
                                onChange={e => setNewProviderCategory(e.target.value)}
                                placeholder="Ej. Mantenimiento, Servicios"
                            />
                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => setShowNewProvider(false)}>Cancelar</button>
                                <button type="submit" className="btn-confirm">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {successNotification && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoCheckmarkCircleOutline size={60} color="#10b981" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem' }}>
                            {typeof successNotification === 'object' ? successNotification.title : '¡Éxito!'}
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem', whiteSpace: 'pre-line' }}>
                            {typeof successNotification === 'object' ? successNotification.message : successNotification}
                        </p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '10px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }} 
                            onClick={() => setSuccessNotification(null)}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            {errorNotification && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#ef4444" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem' }}>Error</h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>{errorNotification}</p>
                        <button 
                            className="btn-confirm" 
                            style={{ width: '100%', padding: '10px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444' }} 
                            onClick={() => setErrorNotification(null)}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            {deleteConfirmId && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem' }}>¿Eliminar Borrador?</h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>¿Estás seguro de eliminar este borrador? Esta acción no se puede deshacer y liberará los archivos asociados.</p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '10px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }} 
                                onClick={() => setDeleteConfirmId(null)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '10px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', borderColor: '#ef4444', color: 'white', cursor: 'pointer' }} 
                                onClick={async () => {
                                    const id = deleteConfirmId;
                                    setDeleteConfirmId(null);
                                    setLoading(true);
                                    try {
                                        await deleteDoc(doc(db, "gastos", id));
                                    } catch (error) {
                                        console.error(error);
                                        setErrorNotification("Error al eliminar borrador: " + error.message);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showScanConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem' }}>¿Re-escanear Documento?</h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>¿Reemplazar los datos actuales con el escaneo automático de IA?</p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '10px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }} 
                                onClick={() => setShowScanConfirm(false)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '10px', fontSize: '1rem', fontWeight: 'bold', background: '#3b82f6', borderColor: '#3b82f6', color: 'white', cursor: 'pointer' }} 
                                onClick={() => {
                                    setShowScanConfirm(false);
                                    handleAutoScan(true);
                                }}
                            >
                                Reemplazar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteProviderConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
                        <IoWarningOutline size={60} color="#f59e0b" style={{ marginBottom: '15px', display: 'inline-block' }} />
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem' }}>¿Eliminar Proveedor?</h3>
                        <p style={{ color: '#4b5563', marginBottom: '20px', fontSize: '0.95rem' }}>
                            ¿Eliminar a <strong>{deleteProviderConfirm.nombre}</strong> del directorio? Esto no borrará sus facturas pasadas.
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '10px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }} 
                                onClick={() => setDeleteProviderConfirm(null)}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '10px', fontSize: '1rem', fontWeight: 'bold', background: '#ef4444', borderColor: '#ef4444', color: 'white', cursor: 'pointer' }} 
                                onClick={async () => {
                                    const { id } = deleteProviderConfirm;
                                    setDeleteProviderConfirm(null);
                                    try {
                                        await deleteDoc(doc(db, "condominio_proveedores", id));
                                    } catch (e) {
                                        console.error(e);
                                        setErrorNotification("Error al eliminar proveedor: " + e.message);
                                    }
                                }}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showApproveConfirm && invoiceToApprove && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '30px' }}>
                        <div style={{ 
                            background: '#ecfdf5', 
                            borderRadius: '50%', 
                            width: '80px', 
                            height: '80px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            margin: '0 auto 20px auto' 
                        }}>
                            <IoReceiptOutline size={40} color="#10b981" />
                        </div>
                        <h3 style={{ marginBottom: '10px', color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                            ¿Confirmar Aprobación?
                        </h3>
                        <p style={{ color: '#4b5563', marginBottom: '25px', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            ¿Estás seguro de aprobar la factura de <strong>{invoiceToApprove.proveedor || invoiceToApprove.proveedorNombre}</strong> por un monto de <strong>${parseFloat(invoiceToApprove.monto || invoiceToApprove.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>?
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button 
                                className="btn-cancel" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={() => {
                                    setShowApproveConfirm(false);
                                    setInvoiceToApprove(null);
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-confirm" 
                                style={{ flex: 1, padding: '12px', fontSize: '1rem', fontWeight: 'bold', background: '#10b981', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '10px' }} 
                                onClick={executeApproval}
                            >
                                Aprobar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Styles for simple modals that might rely on global CSS, keeping basic structure */}
            <style>{`
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justifyContent: center; z-index: 1000; }
                .modal-content { background: white; padding: 25px; borderRadius: 16px; width: 400px; max-width: 90%; }
                .modal-form { display: flex; flexDirection: column; gap: 12px; margin-top: 15px; }
                .modal-actions { display: flex; justifyContent: flex-end; gap: 10px; margin-top: 20px; }
                .btn-cancel { background: #f3f4f6; border: none; padding: 10px 15px; borderRadius: 8px; cursor: pointer; }
                .btn-confirm { background: #10b981; color: white; border: none; padding: 10px 15px; borderRadius: 8px; cursor: pointer; }
                .action-btn-icon { background: white; border: 1px solid #e5e7eb; padding: 6px; borderRadius: 6px; cursor: pointer; color: #4b5563; }
                .action-btn-icon:hover { background: #f9fafb; color: #111827; }
                .action-btn-icon.success:hover { color: green; background: #ecfdf5; }
                .action-btn-icon.danger:hover { color: red; background: #fef2f2; }
                .drag-active-overlay { position: absolute; top:0; left:0; right:0; bottom:0; background: rgba(16, 185, 129, 0.9); z-index: 50; display: flex; align-items: center; justifyContent: center; borderRadius: 20px; }
                .drag-content { color: white; text-align: center; }
            `}</style>
                    </div>
                </div>
                <KondoBottomNav />
            </main>
        </div>
    );
}

export default GestionFacturasScreen;
